/**
 * Life4Earth V2 — My RACI Tasks (multi-project composer)  — DEV-1368
 *
 * Composes the current user's Gantt-task RACI assignments ACROSS all of their
 * full-project roles into a flat list, so the Action Center can surface
 * A / C / I (and R) tasks both inside each role and in the cross-role
 * "All Actions" view.
 *
 * This is the multi-project sibling of `useRoleDigestData` (which is scoped to
 * a single project + role slot and powers the Engagements Role Digest). The
 * composition logic mirrors that hook deliberately so the two stay consistent;
 * this version is READ-ONLY (no mutations) — the "show now, actions next"
 * scope (Skip 2026-06-23). Per-task actions (respond / acknowledge / mark done
 * / flag) reuse `useRoleDigestData` in the follow-up pass.
 *
 * Gantt fetches share the same `fetchForKey` cache keys as `useRoleDigestData`
 * (`ganttTasks:{guid}`, `taskRoleAssignments:{guid}`) so the per-project data
 * is fetched once and reused across both surfaces.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { fetchGanttTasksByProject, type GanttTaskResponse } from "./ganttTasks";
import {
  fetchTaskRoleAssignments,
  buildTaskRoleAssignmentMap,
  type TaskRoleAssignmentEnriched,
} from "./ganttTaskRoleAssignments";
import { fetchDigestAcknowledgments } from "./digestAcknowledgments";
import { fetchTaskConsultations } from "./taskConsultations";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export type RaciLetter = "R" | "A" | "C" | "I";
export type RaciUrgency = "overdue" | "imminent" | "upcoming" | "informational";

/** One RACI-classified Gantt task for the current user, project + role tagged. */
export interface MyRaciTask {
  /** Gantt task GUID. */
  taskGuid: string;
  projectGuid: string;
  roleSlotGuid: string;
  taskName: string;
  wbsPath: string;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  raci: RaciLetter;
  urgency: RaciUrgency;
  /** Done semantics differ per RACI type — precomputed here. */
  done: boolean;
  /** I-mode: acknowledged?  C-mode: input provided?  (drive section + tray). */
  acknowledged: boolean;
  inputProvided: boolean;
  consultationResponseText: string | null;
  /** A/C-mode: who is the Responsible party (read-out). */
  assignedToName: string | null;
  daysSinceUpdate: number | null;
}

export interface UseMyRaciTasksResult {
  tasks: MyRaciTask[];
  loading: boolean;
}

export interface RaciRoleRef {
  projectGuid: string;
  roleSlotGuid: string;
}

// ── Local helpers (mirrors useRoleDigestData's private helpers) ──

const TODAY = () => new Date().toISOString().slice(0, 10);

function daysBetween(dateStr: string | null, reference: string): number | null {
  if (!dateStr) return null;
  return Math.ceil(
    (new Date(dateStr).getTime() - new Date(reference).getTime()) / (1000 * 60 * 60 * 24),
  );
}

function raciChar(type: string): RaciLetter {
  switch (type.toLowerCase()) {
    case "responsible":
      return "R";
    case "accountable":
      return "A";
    case "consulted":
      return "C";
    case "informed":
      return "I";
    default:
      return "R";
  }
}

function computeUrgency(task: GanttTaskResponse, raci: RaciLetter): RaciUrgency {
  if (raci === "I") return "informational";
  if (task.percentComplete >= 100) return "upcoming";
  const daysToEnd = daysBetween(task.endDate, TODAY());
  if (daysToEnd !== null && daysToEnd < 0) return "overdue";
  if (daysToEnd !== null && daysToEnd <= 2) return "imminent";
  return "upcoming";
}

/** WBS path label: nearest summary ancestor by sort order + indent. */
function resolveWbsPath(
  task: GanttTaskResponse,
  taskMap: Map<string, GanttTaskResponse>,
): string {
  const sorted = Array.from(taskMap.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  const idx = sorted.findIndex((t) => t.id.toUpperCase() === task.id.toUpperCase());
  if (idx < 0) return "";
  for (let i = idx - 1; i >= 0; i--) {
    const c = sorted[i];
    if (c.isSummary && c.indentLevel < task.indentLevel) return c.taskName;
  }
  return "";
}

// ── Hook ─────────────────────────────────────────────────────────

export function useMyRaciTasks(
  roles: RaciRoleRef[],
  currentUserGuid: string,
): UseMyRaciTasksResult {
  const [tasks, setTasks] = useState<MyRaciTask[]>([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  // Stable signature so the effect only re-runs when the role set changes.
  const sig = useMemo(
    () =>
      roles
        .map((r) => `${r.projectGuid.toUpperCase()}:${r.roleSlotGuid.toUpperCase()}`)
        .sort()
        .join("|"),
    [roles],
  );

  useEffect(() => {
    mounted.current = true;
    const run = async () => {
      if (!currentUserGuid || roles.length === 0) {
        if (mounted.current) {
          setTasks([]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);

      // Group the user's role slots by project (a project is fetched once).
      const slotsByProject = new Map<string, Set<string>>();
      for (const r of roles) {
        if (!r.projectGuid || !r.roleSlotGuid) continue;
        const pg = r.projectGuid.toUpperCase();
        const set = slotsByProject.get(pg) ?? new Set<string>();
        set.add(r.roleSlotGuid.toUpperCase());
        slotsByProject.set(pg, set);
      }

      const out: MyRaciTask[] = [];
      const today = TODAY();

      await Promise.all(
        Array.from(slotsByProject.entries()).map(async ([projGuid, slotSet]) => {
          try {
            const [tasksResp, assignsResp] = await Promise.all([
              fetchForKey<GanttTaskResponse[]>(`ganttTasks:${projGuid}`, () =>
                fetchGanttTasksByProject(projGuid),
              ),
              fetchForKey<TaskRoleAssignmentEnriched[]>(
                `taskRoleAssignments:${projGuid}`,
                () => fetchTaskRoleAssignments(projGuid),
              ),
            ]);
            const ganttTasks = tasksResp ?? [];
            const assignments = assignsResp ?? [];
            if (ganttTasks.length === 0) return;

            // Supplemental — fail soft.
            const [acks, consults] = await Promise.all([
              fetchDigestAcknowledgments(projGuid, currentUserGuid).catch(() => []),
              fetchTaskConsultations(projGuid, currentUserGuid).catch(() => []),
            ]);

            const taskMap = new Map<string, GanttTaskResponse>();
            for (const t of ganttTasks) taskMap.set(t.id.toUpperCase(), t);
            const assignmentMap = buildTaskRoleAssignmentMap(assignments);

            const ackSet = new Set<string>();
            for (const a of acks) {
              ackSet.add(`${a.ganttTaskId.toUpperCase()}:${a.roleSlotGuid.toUpperCase()}`);
            }
            const consultMap = new Map<string, string>();
            for (const c of consults) {
              consultMap.set(
                `${c.ganttTaskId.toUpperCase()}:${c.roleSlotGuid.toUpperCase()}`,
                c.responseText,
              );
            }

            // The user's assignments on this project (their role slots only).
            const mine = assignments.filter((a) => slotSet.has(a.roleSlotGuid.toUpperCase()));
            for (const assign of mine) {
              const taskKey = assign.ganttTaskId.toUpperCase();
              const task = taskMap.get(taskKey);
              if (!task || task.isSummary) continue;
              const raci = raciChar(assign.responsibilityType);
              const urgency = computeUrgency(task, raci);
              const slotKey = `${taskKey}:${assign.roleSlotGuid.toUpperCase()}`;
              const acknowledged = ackSet.has(slotKey);
              const inputProvided = consultMap.has(slotKey);

              // Done differs by RACI type.
              const done =
                raci === "I"
                  ? acknowledged
                  : raci === "C"
                    ? inputProvided
                    : task.percentComplete >= 100;

              // R-person read-out for A / C items.
              let assignedToName: string | null = null;
              let daysSinceUpdate: number | null = null;
              if (raci === "A" || raci === "C") {
                const rAssign = (assignmentMap.get(taskKey) ?? []).find(
                  (a) => a.responsibilityType === "responsible",
                );
                if (rAssign) {
                  assignedToName = rAssign.roleLabel;
                  // Days since the R person last touched the task (staleness).
                  daysSinceUpdate = task.updatedAt
                    ? Math.max(0, daysBetween(today, task.updatedAt) ?? 0)
                    : null;
                }
              }

              out.push({
                taskGuid: task.id,
                projectGuid: projGuid,
                roleSlotGuid: assign.roleSlotGuid,
                taskName: task.taskName,
                wbsPath: resolveWbsPath(task, taskMap),
                startDate: task.startDate,
                endDate: task.endDate,
                percentComplete: task.percentComplete,
                raci,
                urgency,
                done,
                acknowledged,
                inputProvided,
                consultationResponseText: consultMap.get(slotKey) ?? null,
                assignedToName,
                daysSinceUpdate,
              });
            }
          } catch {
            // One project failing shouldn't sink the whole list.
          }
        }),
      );

      if (mounted.current) {
        setTasks(out);
        setLoading(false);
      }
    };

    run();
    window.addEventListener(CACHE_UPDATE_EVENT, run);
    return () => {
      mounted.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, run);
    };
    // `sig` is the stable serialization of `roles`; depending on it (not the
    // array identity) prevents a refetch loop from a new array each render.
    // `roles` is intentionally omitted in favor of `sig`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, currentUserGuid]);

  return { tasks, loading };
}
